import "../styles/variables.css";
import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { GetStartedButton } from "../components/common/GetStartedButton";

const AnimatedNumber = ({ target, duration = 2000 }) => {
  const [count, setCount] = useState(0);
  const [isVisible, setIsVisible] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.disconnect(); // Only trigger once
        }
      },
      { threshold: 0.3 } // start when 30% visible
    );

    if (ref.current) observer.observe(ref.current);

    return () => {
      if (ref.current) observer.unobserve(ref.current);
    };
  }, []);

  useEffect(() => {
    if (!isVisible) return;

    let start = 0;
    const end = parseInt(target);
    const increment = Math.ceil(end / (duration / 10)); // jump faster for large numbers
    const timer = setInterval(() => {
      start += increment;
      if (start >= end) {
        start = end;
        clearInterval(timer);
      }
      setCount(start);
    }, 10);

    return () => clearInterval(timer);
  }, [isVisible, target, duration]);

  return (
    <span ref={ref} className="inline-block">
      {count.toLocaleString()}+
    </span>
  );
};

const STATS = [
  { target: 15000, label: "Junior college athletes in our database" },
  { target: 450, duration: 1200, label: "Junior college baseball teams in our database" },
  { target: 13000, duration: 1000, label: "Followers across social media channels" },
  { target: 40000, label: "Videos preloaded" },
];

const AboutUs = () => {
  const navigate = useNavigate();

  return (
    <main className="min-h-screen bg-jp-page text-jp-ink text-left overflow-x-hidden">
      {/* Header band: navy in both themes so the white navbar stays readable */}
      <section className="bg-jp-band px-4 sm:px-6 lg:px-8 pt-32 sm:pt-36 pb-14 sm:pb-16">
        <div className="max-w-7xl mx-auto">
          <p className="text-yellow-300 text-sm sm:text-base font-bold uppercase tracking-widest mb-4">
            Who We Are
          </p>
          <h1 className="text-5xl sm:text-6xl lg:text-7xl font-anton leading-[1.1] tracking-tight text-white">
            ABOUT{" "}
            <span className="bg-gradient-to-r from-cyan-400 via-blue-400 to-cyan-500 text-transparent bg-clip-text">
              US
            </span>
          </h1>
        </div>
      </section>

      {/* Mission */}
      <section className="bg-jp-page px-4 sm:px-6 lg:px-8 py-16 sm:py-20">
        <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-[1fr_1.4fr] gap-8 lg:gap-16 items-start">
          <div>
            <p className="text-jp-muted dark:text-jp-gold text-xs font-bold uppercase tracking-widest mb-2">
              Our Mission
            </p>
            <h2 className="text-3xl sm:text-4xl font-extrabold text-jp-ink mb-6">
              JUCO Pipeline is a <span className="text-jp-accent">recruiting platform</span>
            </h2>
            <div className="hidden lg:block">
              <GetStartedButton onClick={() => navigate("/auth/signup")} />
            </div>
          </div>

          <div className="lg:border-l-4 lg:border-jp-accent lg:pl-10">
            <p className="text-xl sm:text-2xl font-semibold text-jp-ink leading-snug mb-5">
              Designed to bridge the gap between junior college athletes and four-year programs
              across the country.
            </p>
            <p className="text-jp-muted text-base sm:text-lg leading-relaxed mb-6 lg:mb-0">
              Our mission is to give uncommitted players the exposure they deserve, while providing
              coaches and scouts with powerful tools to discover, track, and connect with talent —
              all in one place.
            </p>
            <div className="lg:hidden">
              <GetStartedButton onClick={() => navigate("/auth/signup")} />
            </div>
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="bg-jp-section border-y border-jp-line px-4 sm:px-6 lg:px-8 py-16 sm:py-20">
        <div className="max-w-7xl mx-auto">
          <p className="text-jp-muted dark:text-jp-gold text-xs font-bold uppercase tracking-widest mb-2">
            By The Numbers
          </p>
          <h2 className="text-2xl sm:text-3xl font-extrabold text-jp-ink mb-6">
            The JUCO Pipeline <span className="text-jp-accent">Network</span>
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 bg-jp-card rounded-2xl border border-jp-outline/70 shadow-sm p-4">
            {STATS.map((stat) => (
              <div key={stat.label} className="rounded-xl p-5 bg-jp-section border border-jp-outline/50">
                <p className="font-anton text-4xl sm:text-5xl leading-none tracking-tight text-jp-accent mb-3">
                  <AnimatedNumber target={stat.target} duration={stat.duration} />
                </p>
                <p className="text-jp-muted text-xs font-bold uppercase tracking-wider leading-relaxed">
                  {stat.label}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Founder */}
      <section className="bg-jp-page px-4 sm:px-6 lg:px-8 py-16 sm:py-20">
        <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-[1fr_2fr] gap-6 lg:gap-10">
          <div>
            <p className="text-jp-muted dark:text-jp-gold text-xs font-bold uppercase tracking-widest mb-2">
              Meet the Founder
            </p>
            <h2 className="text-2xl sm:text-3xl font-extrabold text-jp-ink">
              Built From Experience. <span className="text-jp-accent">Driven by Opportunity.</span>
            </h2>
          </div>
          <div className="bg-jp-card rounded-2xl border border-jp-outline/70 shadow-sm p-4">
            <div className="rounded-xl p-5 sm:p-6 bg-jp-section border border-jp-outline/50 space-y-4 text-jp-muted leading-relaxed">
              <p>
                Through working with Santa Fe College Baseball, the Hyannis Harbor Hawks of the Cape Cod
                Baseball League, and Florida Gators Baseball, I gained experience in baseball operations,
                scouting, and analytics. Those experiences helped me recognize the importance of accessible
                player information and efficient recruiting tools.
              </p>
              <p>
                I created JUCO Pipeline to connect junior college players with opportunities to continue
                their playing careers while helping college coaches find the talent that fits their
                programs. By bringing player profiles, sortable statistics, video, and commitment
                information into one platform, JUCO Pipeline makes it easier for players to get discovered
                and for coaches to make informed recruiting decisions.
              </p>
              <p className="text-jp-ink font-semibold">
                My goal is to make JUCO baseball talent easier to find and give players another avenue to
                be discovered.
              </p>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
};

export default AboutUs;
